/**
 * Push notification client
 * Handles push subscription management and state
 */

import { base64UrlToUint8Array } from './helpers.js';

export class PushClient {
  constructor(stateChangeCallback, subscriptionUpdateCallback, publicApplicationKey) {
    this.stateChangeCallback = stateChangeCallback;
    this.subscriptionUpdateCallback = subscriptionUpdateCallback;
    this.publicApplicationKey = base64UrlToUint8Array(publicApplicationKey);

    // Debounce settings for subscription verification
    this.verificationDebounceTime = 5000; // Augmenté à 5 secondes
    this.verificationTimeout = null;
    this.lastVerificationTime = 0;
    this.isVerifying = false; // Flag pour éviter les appels concurrents

    // Define possible states
    this.states = {
      UNSUPPORTED: { id: 'UNSUPPORTED', interactive: false, pushEnabled: false },
      INITIALISING: { id: 'INITIALISING', interactive: false, pushEnabled: false },
      PERMISSION_DENIED: { id: 'PERMISSION_DENIED', interactive: false, pushEnabled: false },
      PERMISSION_GRANTED: { id: 'PERMISSION_GRANTED', interactive: true },
      PERMISSION_PROMPT: { id: 'PERMISSION_PROMPT', interactive: true, pushEnabled: false },
      ERROR: { id: 'ERROR', interactive: false, pushEnabled: false },
      STARTING_SUBSCRIBE: { id: 'STARTING_SUBSCRIBE', interactive: false, pushEnabled: true },
      SUBSCRIBED: { id: 'SUBSCRIBED', interactive: true, pushEnabled: true },
      STARTING_UNSUBSCRIBE: { id: 'STARTING_UNSUBSCRIBE', interactive: false, pushEnabled: false },
      UNSUBSCRIBED: { id: 'UNSUBSCRIBED', interactive: true, pushEnabled: false }
    };

    // Check for browser support
    if (!('serviceWorker' in navigator)) {
      this.stateChangeCallback(this.states.UNSUPPORTED, 'Service Worker not available in this browser');
      return;
    }

    if (!('PushManager' in window)) {
      this.stateChangeCallback(this.states.UNSUPPORTED, 'Push messaging not available in this browser');
      return;
    }

    if (!('showNotification' in ServiceWorkerRegistration.prototype)) {
      this.stateChangeCallback(this.states.UNSUPPORTED, 'Notifications not available in this browser');
      return;
    }

    this.init();
  }

  async init() {
    try {
      await navigator.serviceWorker.ready;
      this.stateChangeCallback(this.states.INITIALISING);
      await this.setupPushPermission();
    } catch (error) {
      console.error('Push client initialization error:', error);
      this.stateChangeCallback(this.states.ERROR, error);
    }
  }

  async setupPushPermission() {
    try {
      this.updatePermissionState(Notification.permission);

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      this.subscriptionUpdateCallback(subscription);

      if (!subscription) {
        return; // No subscription, permission state will control UI
      }

      // Vérifier si la subscription est toujours active dans le backend
      const isStillActive = await this.verifySubscriptionWithBackendDebounced(subscription);

      if (isStillActive === false) { // Explicitement false, pas null/undefined
        // Désabonner localement puisque le backend ne reconnaît plus cette subscription
        await subscription.unsubscribe();
        this.subscriptionUpdateCallback(null);
        this.updatePermissionState(Notification.permission);
        return;
      }

      this.stateChangeCallback(this.states.SUBSCRIBED);
    } catch (error) {
      console.error('Setup push permission error:', error);
      this.stateChangeCallback(this.states.ERROR, error);
    }
  }

  updatePermissionState(permissionState) {
    switch (permissionState) {
      case 'denied':
        this.stateChangeCallback(this.states.PERMISSION_DENIED);
        break;
      case 'granted':
        this.stateChangeCallback(this.states.PERMISSION_GRANTED);
        break;
      case 'default':
        this.stateChangeCallback(this.states.PERMISSION_PROMPT);
        break;
      default:
        console.error('Unexpected permission state:', permissionState);
        break;
    }
  }

  // Vérifier la subscription en utilisant l'endpoint existant /subscriptions
  async verifySubscriptionWithBackend(subscription) {
    // Protection contre les appels concurrents
    if (this.isVerifying) {
      console.log('Verification already in progress, skipping...');
      return true;
    }

    this.isVerifying = true;

    try {
      console.log('🔍 Verifying subscription with backend...');

      const response = await fetch('/api/push/subscriptions', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(this.getAuthHeader && this.getAuthHeader())
        }
      });

      if (!response.ok) {
        console.warn('Could not verify subscription with backend:', response.status);
        return true; // En cas d'erreur, on assume que c'est OK
      }

      const userSubscriptions = await response.json();

      // Vérifier si notre endpoint actuel existe dans les subscriptions du backend
      const subscriptionExists = userSubscriptions.some(
        backendSub => backendSub.endpoint === subscription.endpoint && backendSub.is_active
      );

      console.log(`✅ Subscription verification result: ${subscriptionExists ? 'found' : 'not found'}`);
      return subscriptionExists;
    } catch (error) {
      console.warn('Error verifying subscription with backend:', error);
      // En cas d'erreur de réseau, on assume que c'est OK pour éviter de tout casser
      return true;
    } finally {
      this.isVerifying = false;
    }
  }

  // Debounced version of verifySubscriptionWithBackend
  async verifySubscriptionWithBackendDebounced(subscription) {
    const now = Date.now();

    // Si une vérification a eu lieu récemment, l'ignorer
    if (now - this.lastVerificationTime < this.verificationDebounceTime) {
      console.log('Verification skipped due to debounce');
      return true; // Assume true pour éviter les désabonnements non voulus
    }

    // Si une vérification est déjà en cours, l'attendre
    if (this.isVerifying) {
      console.log('Verification already in progress, waiting...');
      // Attendre que la vérification en cours se termine
      let attempts = 0;
      while (this.isVerifying && attempts < 50) { // Max 5 secondes d'attente
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      return true; // Assume true après l'attente
    }

    // Annuler toute vérification en attente
    if (this.verificationTimeout) {
      clearTimeout(this.verificationTimeout);
    }

    return new Promise((resolve) => {
      this.verificationTimeout = setTimeout(async () => {
        try {
          this.lastVerificationTime = Date.now();
          const result = await this.verifySubscriptionWithBackend(subscription);
          resolve(result);
        } catch (error) {
          console.error('Debounced verification error:', error);
          resolve(true); // En cas d'erreur, assume true
        }
      }, 1000); // Attendre 1 seconde avant la vérification
    });
  }  async subscribeDevice() {
    this.stateChangeCallback(this.states.STARTING_SUBSCRIBE);

    try {
      // Handle permission
      switch (Notification.permission) {
        case 'denied':
          throw new Error('Push notifications are blocked');
        case 'granted':
          break;
        default:
          // Request permission
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') {
            throw new Error('Permission denied for notifications');
          }
          break;
      }

      // Subscribe to push service
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.publicApplicationKey
      });

      // Send subscription to backend
      await this.sendSubscriptionToBackend(subscription);

      this.stateChangeCallback(this.states.SUBSCRIBED);
      this.subscriptionUpdateCallback(subscription);

      return subscription;
    } catch (error) {
      console.error('Subscribe device error:', error);
      this.stateChangeCallback(this.states.ERROR, error);
      this.updatePermissionState(Notification.permission);
      throw error;
    }
  }

  async unsubscribeDevice() {
    this.stateChangeCallback(this.states.STARTING_UNSUBSCRIBE);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        this.stateChangeCallback(this.states.UNSUBSCRIBED);
        this.subscriptionUpdateCallback(null);
        return;
      }

      // Remove subscription from backend
      await this.removeSubscriptionFromBackend(subscription);

      const successful = await subscription.unsubscribe();
      if (!successful) {
        console.warn('Unable to unregister from push service');
      }

      this.stateChangeCallback(this.states.UNSUBSCRIBED);
      this.subscriptionUpdateCallback(null);
    } catch (error) {
      console.error('Unsubscribe device error:', error);
      this.stateChangeCallback(this.states.ERROR, error);
    }
  }

  // Send subscription to backend API
  async sendSubscriptionToBackend(subscription) {
    try {
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if user is logged in
          ...(this.getAuthHeader && this.getAuthHeader())
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: subscription.getKey('p256dh') ?
              btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))) : null,
            auth: subscription.getKey('auth') ?
              btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))) : null
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save subscription: ${response.status}`);
      }

      await response.json();
    } catch (error) {
      console.error('Error saving subscription to backend:', error);
      throw error;
    }
  }

  // Remove subscription from backend API
  async removeSubscriptionFromBackend(subscription) {
    try {
      const response = await fetch('/api/push/unsubscribe', {
        method: 'DELETE',
        credentials: 'include', // Include cookies for authentication
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if user is logged in
          ...(this.getAuthHeader && this.getAuthHeader())
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      });

      if (!response.ok) {
        console.warn(`Failed to remove subscription from backend: ${response.status}`);
      } else {
        console.log('Subscription removed from backend');
      }
    } catch (error) {
      console.error('Error removing subscription from backend:', error);
    }
  }

  // Set authentication header provider
  setAuthHeaderProvider(getAuthHeader) {
    this.getAuthHeader = getAuthHeader;
  }

  // Get current subscription
  async getSubscription() {
    try {
      const registration = await navigator.serviceWorker.ready;
      return await registration.pushManager.getSubscription();
    } catch (error) {
      console.error('Get subscription error:', error);
      return null;
    }
  }

  // Check if push is supported
  static isSupported() {
    return 'serviceWorker' in navigator &&
           'PushManager' in window &&
           'showNotification' in ServiceWorkerRegistration.prototype;
  }
}
